import { ListingService } from './../../../jobs-module/services/listing.service';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { CareerSubDomain } from '../../../lib/classes/careerSubDomain';
import { take } from 'rxjs/operators';
import { ModalService } from '../../../lib/services/modal.service';
import { SelectPlanComponent } from '../../../billing-module/components/select-plan/select-plan.component';
import { BillingService } from '../../../lib/services/billingService';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../reducers';
import { selectPaymentState } from '../../../core/selectors/paymentState';
import { Unit } from '../../models/unit';
import { CareerListing } from '../career-page/career-page.model';

@Component({
  selector: 'mj-career-page-send-container',
  templateUrl: './mj-career-page-send-container.component.html',
  styleUrls: ['./mj-career-page-send-container.component.scss'],
})
export class MjCareerPageSendContainerComponent implements OnInit, OnChanges {
  @Input() unit: Unit;
  @Input() listing: CareerListing;

  @Output() onNewListingCreated = new EventEmitter<CareerListing>();

  userSubs;

  subdomainEnabled = false;
  savingInProgress = false;

  canActivateCareer: boolean;

  newSubdomain = '';

  constructor(
    private _translate: TranslateService,
    private _snackBar: MatSnackBar,
    private listingService: ListingService,
    private modalService: ModalService,
    private billingService: BillingService,
    private store: Store<fromRoot.State>
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['listing']) {
      this.assignSubdomain();
    }
  }

  ngOnInit(): void {
    this.assignSubdomain();
  }

  assignSubdomain(): void {
    this.checkPayment();
    if (this.listing.career_page_subdomain) {
      this.subdomainEnabled = true;
      this.newSubdomain = this.listing.career_page_subdomain.subdomain;
    }
  }

  saveListing() {
    this.savingInProgress = true;
    this.listingService.createUpdateCareerListing(this.listing).subscribe(
      data => {
        this.saveSubdomainChanges(data);
      },
      error => {
        this.showSavingFailure();
      }
    );
  }

  checkPayment() {
    const unitId = localStorage.getItem('unit_id');
    this.billingService
      .fetchSubscriptions(unitId)
      .pipe(take(1))
      .subscribe(subs => {
        this.store
          .select(selectPaymentState)
          .pipe(take(1))
          .subscribe(payment => {
            this.userSubs = subs.data;

            const hasProPlan =
              this.userSubs.length &&
              !!this.userSubs[0].plan.tiers[0].flat_amount;
            const hasActivePayment =
              payment.status === 'trialing' ||
              payment.status === 'active' ||
              payment.status === 'always_active' ||
              payment.status === 'pending';

            this.canActivateCareer =
              (hasProPlan && hasActivePayment) ||
              payment.status === 'always_active';

            if (!this.canActivateCareer) {
              this.subdomainEnabled = false;
            }
          });
      });
  }

  saveSubdomainChanges(listing: CareerListing) {
    if (listing.career_page_subdomain) {
      if (this.listing.career_page_subdomain.id) {
        if (
          listing.career_page_subdomain.subdomain !== this.newSubdomain &&
          this.subdomainEnabled
        ) {
          listing.career_page_subdomain.subdomain = this.newSubdomain;
          if (this.listing.career_page_subdomain.id) {
            this.listingService
              .deleteCareerSubdomain(this.listing.career_page_subdomain)
              .subscribe(
                _ => {
                  const newSubdomainInput = new CareerSubDomain({
                    subdomain: this.newSubdomain,
                    career_page_id: listing.id,
                  });
                  this.listingService
                    .createCareerSubdomain(newSubdomainInput)
                    .subscribe(
                      data => {
                        this.listingService
                          .getCareerListing(this.unit.id)
                          .subscribe(
                            storedListing => {
                              this.onNewListingCreated.emit(storedListing);
                            },
                            error => {
                              this.onNewListingCreated.emit(
                                CareerListing.createEmptyCareerListing(
                                  this.unit.id
                                )
                              );
                            }
                          );
                        this.showSavingSuccess();
                      },
                      error => {
                        this.showSavingFailure();
                      }
                    );
                },
                error => {
                  this.showSavingFailure();
                }
              );
          } else {
            this.showSavingSuccess();
          }
        } else if (!this.subdomainEnabled) {
          this.listingService
            .deleteCareerSubdomain(this.listing.career_page_subdomain)
            .subscribe(
              data => {
                this.listingService.getCareerListing(this.unit.id).subscribe(
                  storedListing => {
                    this.onNewListingCreated.emit(storedListing);
                  },
                  error => {
                    this.onNewListingCreated.emit(
                      CareerListing.createEmptyCareerListing(this.unit.id)
                    );
                  }
                );
                this.showSavingSuccess();
              },
              error => {
                this.showSavingFailure();
              }
            );
        } else {
          this.onNewListingCreated.emit(listing);
          this.showSavingSuccess();
        }
      }
    } else if (this.subdomainEnabled) {
      const newSubdomainInput = new CareerSubDomain({
        subdomain: this.newSubdomain,
        career_page_id: listing.id,
      });
      this.listingService.createCareerSubdomain(newSubdomainInput).subscribe(
        data => {
          this.listingService.getCareerListing(this.unit.id).subscribe(
            storedListing => {
              this.onNewListingCreated.emit(storedListing);
            },
            error => {
              this.onNewListingCreated.emit(
                CareerListing.createEmptyCareerListing(this.unit.id)
              );
            }
          );
          this.showSavingSuccess();
        },
        error => {
          this.showSavingFailure();
        }
      );
    } else {
      this.showSavingSuccess();
    }
  }

  private showSavingFailure() {
    this.savingInProgress = false;
    this._translate
      .get('settings_career_side_saving_failure')
      .subscribe(res => {
        this._snackBar.open(res, null, {
          duration: 3000,
        });
      });
  }

  private showSavingSuccess() {
    this.savingInProgress = false;
    this._translate
      .get('settings_career_side_saved_successfully')
      .subscribe(res => {
        this._snackBar.open(res, null, {
          duration: 3000,
        });
      });
  }

  copyUrl() {
    // Create new element
    const el = document.createElement('textarea');
    // Set value (string to be copied)
      el.value = `https://${this.newSubdomain}.mojob.io`;
    // Set non-editable to avoid focus and move outside of view
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    // Select text inside element
    el.select();
    // Copy text to clipboard
    document.execCommand('copy');
    // Remove temporary element
    document.body.removeChild(el);
  }

  onSubdomainClicked() {
    if (!this.subdomainEnabled && !this.canActivateCareer) {
      this.modalService
        .appendComponentToBody(SelectPlanComponent, {
          userSubs: this.userSubs,
        })
        .pipe(take(1))
        .subscribe(res => {
          this.checkPayment();
        });
    }
  }
}
